// Editor-first landing (C0-R). Rendered at "/" behind ProtectedRoute (so `me` is
// guaranteed). Resolves where to drop the user IN THE WORK:
//   1. their last-opened model, if it's still a live ACTIVE model
//   2. else the most-recently-updated active model
//   3. else (zero models) auto-create "Untitled" — the first model is free, so no
//      402 — and open it. The user never lands on an empty page.
// All navigations REPLACE so "/" doesn't linger in history (no back-button loop /
// re-trigger). A synchronous `started` ref guards against React 18 StrictMode's
// double-invoke firing two createModel calls (which would make two "Untitled"s).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import FullScreenSpinner from '../components/FullScreenSpinner';
import { Button } from '../components/ui/button';
import { errorMessage } from '../lib/errors';

// Module-scoped so it SURVIVES React 18 StrictMode's mount→unmount→mount: a fresh
// useRef would reset on the remount and let the resolver run twice (→ two
// "Untitled" models). The finally clears it, so a real later visit re-resolves.
let resolving = false;

export default function LandingRedirect() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async () => {
    // First-run gate: a user who hasn't finished the one-time "choose a plan" step
    // is sent there first (welcome mode), THEN dropped into the editor. Every later
    // login skips this (onboarded !== false) and resolves straight to the editor.
    // Checked before the editor resolve so a brand-new user sees plans, not a
    // spinner + auto-created model. `=== false` (not falsy) so a missing field on
    // an older payload never traps the user out of their workspace.
    if (me?.onboarded === false) {
      navigate('/plans?welcome=1', { replace: true });
      return;
    }
    if (resolving) return;
    resolving = true;
    setError(null);

    const attempt = async () => {
      const active = await api.listModels('active');
      const last = me?.last_opened_model_id;
      if (last && active.some((m) => m.id === last)) {
        navigate(`/editor/${last}`, { replace: true });
        return;
      }
      if (active.length > 0) {
        navigate(`/editor/${active[0].id}`, { replace: true });
        return;
      }
      // createModel is only reached when listModels SUCCEEDED with zero models, so
      // the retry below never double-creates (it re-runs the idempotent GET only).
      const created = await api.createModel({ name: 'Untitled' });
      navigate(`/editor/${created.id}`, { replace: true });
    };

    // Retry with backoff: right after login the session can still be settling, so
    // the first request may transiently 401. Don't dump the user to an error
    // screen for that — give it a beat. Genuine failures surface after the retries.
    for (let i = 0; i < 3; i++) {
      try {
        await attempt();
        resolving = false;
        return;
      } catch (err) {
        if (i === 2) {
          resolving = false;
          setError(errorMessage(err, 'Could not open your workspace.'));
          return;
        }
        await new Promise((r) => setTimeout(r, 350 * (i + 1)));
      }
    }
  }, [me, navigate]);

  useEffect(() => {
    void resolve();
  }, [resolve]);

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background p-8 text-center text-foreground">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button onClick={() => void resolve()}>Try again</Button>
      </div>
    );
  }
  return <FullScreenSpinner />;
}
